import type { DayClassStatus } from '../../types';

/** A calendar day's class entries, as the parent calendar already loads them. */
export interface RangeDayClass {
  classId: string;
  className: string;
  suppressedBy?: string | null;
  attendanceStatus?: DayClassStatus | string | null;
}
export interface RangeDay {
  date: string;
  classes: RangeDayClass[];
}

/** One class on one day that a range-absence will actually file. */
export interface FileableAbsence {
  date: string;
  classId: string;
  className: string;
}

/** A class the parent can include in or leave out of a range absence. */
export interface RangeClassOption {
  classId: string;
  className: string;
}

export interface PlanAbsenceRangeOptions {
  /** When set, only these classes are filed — a parent marking "no after
   *  school this week" while the child still comes to Breakfast Club. */
  classIds?: ReadonlySet<string>;
}

export interface AbsenceRangePlan {
  /** The class-days that WILL be filed (future, scheduled, not suppressed, not already absent). */
  fileable: FileableAbsence[];
  /** Distinct dates in the range that contribute at least one fileable class. */
  fileableDates: string[];
  /** Dates in the range skipped because the child has no class that runs then
   *  (weekend, non-run day, or a closure suppressing every class). */
  skippedNoClassDates: string[];
}

/** Inclusive ISO-date range [start, end] (order-independent), as YYYY-MM-DD. */
export function datesInRange(start: string, end: string): string[] {
  const [a, b] = start <= end ? [start, end] : [end, start];
  const out: string[] = [];
  const cursor = new Date(`${a}T00:00:00Z`);
  const last = new Date(`${b}T00:00:00Z`);
  while (cursor <= last) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/**
 * Work out exactly which class-days an absence over [start, end] will file.
 *
 * A day contributes a class only when the absence is genuinely fileable for it:
 *  - the date is TODAY or later (the backend enforces "today or later"; a
 *    child who wakes up sick is the everyday case, so today is fileable —
 *    an earlier version excluded it here on the belief that the single-day
 *    view handled today, which it did not either);
 *  - the class actually runs that day and isn't suppressed by a
 *    closure/cancellation (`suppressedBy` unset);
 *  - the class carries no attendance status yet — not already a planned
 *    absence, and not a child who is already checked in (or gone) today. The
 *    day panel applies the same rule; with today in scope the two must agree.
 *
 * Everything else in the range is reported back as skipped, so the confirm
 * panel can say plainly what will and won't be marked.
 */
export function planAbsenceRange(
  start: string,
  end: string,
  today: string,
  dayByDate: Map<string, RangeDay>,
  plannedAbsentStatus: DayClassStatus | string,
  options: PlanAbsenceRangeOptions = {}
): AbsenceRangePlan {
  const fileable: FileableAbsence[] = [];
  const fileableDateSet = new Set<string>();
  const skippedNoClassDates: string[] = [];

  for (const date of datesInRange(start, end)) {
    if (date < today) continue; // past days cannot be marked off
    const day = dayByDate.get(date);
    const runnableClasses = (day?.classes ?? []).filter(
      (c) =>
        !c.suppressedBy &&
        !c.attendanceStatus &&
        (options.classIds === undefined || options.classIds.has(c.classId))
    );
    if (runnableClasses.length === 0) {
      // Only call it "skipped" if it was a day within the (future) range that
      // simply has nothing to file — a genuinely empty day for this child.
      skippedNoClassDates.push(date);
      continue;
    }
    for (const c of runnableClasses) {
      fileable.push({ date, classId: c.classId, className: c.className });
      fileableDateSet.add(date);
    }
  }

  return {
    fileable,
    fileableDates: [...fileableDateSet].sort(),
    skippedNoClassDates,
  };
}

/**
 * The distinct classes a plan would file, in first-seen order — the options
 * the confirm panel offers as tick boxes. Compute it from the UNFILTERED plan,
 * or an unticked class disappears from the list and can never be re-ticked.
 */
export function classesInPlan(plan: AbsenceRangePlan): RangeClassOption[] {
  const seen = new Map<string, RangeClassOption>();
  for (const f of plan.fileable) {
    if (!seen.has(f.classId)) seen.set(f.classId, { classId: f.classId, className: f.className });
  }
  return [...seen.values()];
}

/** "12 Aug", "12–15 Aug", "12 Aug – 2 Sep" — a compact range label for the UI. */
export function formatRangeLabel(start: string, end: string): string {
  const [a, b] = start <= end ? [start, end] : [end, start];
  const fmt = (iso: string, withMonth: boolean) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const day = d.getUTCDate();
    const month = d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
    return withMonth ? `${day} ${month}` : `${day}`;
  };
  if (a === b) return fmt(a, true);
  const sameMonth = a.slice(0, 7) === b.slice(0, 7);
  return sameMonth ? `${fmt(a, false)}–${fmt(b, true)}` : `${fmt(a, true)} – ${fmt(b, true)}`;
}
